import { isRejected } from '@reduxjs/toolkit';

import { decline, genericDecline, hardDecline, softDecline } from '~/slices/decline';

import type { RootState } from '../../store';
import trackError from './trackErrors';
import { qalTrackGoalEvent } from './trackEvent';
import { retrieveQuote } from './types';

/**
 * mapActions selects which dispatched actions we are interested in tracking in Google Analytics,
 * firing off the relevant virtual pageview or event to the window.dataLayer array.
 * @param {object} action (mandatory) The Redux Action object being dispatched.
 * @param {object} state (mandatory) The current state of the Redux store.
 * @returns {object} trackPageView or trackEvent.
 */

const mapActions = (action: unknown, state: RootState) => {
  // Type guard to check if action has the expected shape
  if (typeof action !== 'object' || action === null || !('type' in action)) {
    return false;
  }

  const typedAction = action as { type: string; payload?: unknown };
  const { payload, type } = typedAction;

  switch (true) {
    // Tracking Errors, except soft decline is via the component level - SoftDeclineModal.tsx.
    case decline.match(action):
    case genericDecline.match(action):
    case softDecline.match(action):
    case hardDecline.match(action):
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return trackError(payload as any, state);

    // Track API rejected.
    case isRejected(action) && type.includes('Api'):
      return trackError(
        payload && typeof payload === 'object' && 'data' in payload
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (payload as any).data
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (payload as any),
        state
      );

    case type === 'quote/updateRetrieveQuoteFlow':
      return qalTrackGoalEvent(retrieveQuote(payload as string), state);
    default:
      return false;
  }
};

export default mapActions;
